/**
 * ZK Device List Component
 *
 * Manages ZK time clock devices - add, edit, delete, test connection.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Card,
  Alert,
  Result
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  DesktopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { zkDeviceApi, ZkDevice, ZkDeviceInfo } from '../../services/zkService';

dayjs.extend(relativeTime);

const { Text } = Typography;

const ZKDeviceList: React.FC = () => {
  const [devices, setDevices] = useState<ZkDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState<ZkDevice | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [form] = Form.useForm();

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await zkDeviceApi.getAll(true);
      setDevices(data || []);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load devices';
      setError(errorMsg);
      setDevices([]);
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Open modal for new device
  const handleAddDevice = () => {
    setEditingDevice(null);
    form.resetFields();
    form.setFieldsValue({
      port: 4370,
      syncInterval: 10,
      timezone: 'Asia/Hong_Kong',
      isEnabled: true
    });
    setModalVisible(true);
  };

  // Open modal for editing
  const handleEditDevice = (device: ZkDevice) => {
    setEditingDevice(device);
    form.setFieldsValue({
      deviceName: device.deviceName,
      ipAddress: device.ipAddress,
      port: device.port,
      deviceSerial: device.deviceSerial,
      timezone: device.timezone,
      syncInterval: device.syncInterval,
      isEnabled: device.isEnabled
    });
    setModalVisible(true);
  };

  // Test connection
  const handleTestConnection = async (device?: ZkDevice) => {
    if (device) {
      // Test existing device
      setTestingConnection(device.id);
      try {
        const result = await zkDeviceApi.testConnection(device.id);
        if (result.success) {
          message.success(`Connected successfully! Users: ${result.info?.userCount || 0}, Logs: ${result.info?.logCount || 0}`);
        } else {
          message.error(`Connection failed: ${result.message || 'Unknown error'}`);
        }
        fetchDevices(); // Refresh to update status
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || error.message || 'Connection test failed';
        message.error(`Connection test failed: ${errorMsg}`);
      } finally {
        setTestingConnection(null);
      }
    } else {
      // Test from form values
      const values = form.getFieldsValue();
      if (!values.ipAddress) {
        message.warning('Please enter IP address');
        return;
      }
      setTestingConnection('new');
      try {
        const result = await zkDeviceApi.testConnectionByAddress(values.ipAddress, values.port || 4370);
        if (result.success) {
          message.success(`Connected successfully! Users: ${result.info?.userCount || 0}, Logs: ${result.info?.logCount || 0}`);
          // Auto-fill device serial if available
          if (result.info?.serialNumber) {
            form.setFieldValue('deviceSerial', result.info.serialNumber);
          }
        } else {
          message.error(`Connection failed: ${result.message || 'Unknown error'}`);
        }
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || error.message || 'Connection test failed';
        message.error(`Connection test failed: ${errorMsg}`);
      } finally {
        setTestingConnection(null);
      }
    }
  };

  // Save device
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingDevice) {
        await zkDeviceApi.update(editingDevice.id, values);
        message.success('Device updated successfully');
      } else {
        await zkDeviceApi.create(values);
        message.success('Device added successfully');
      }

      setModalVisible(false);
      fetchDevices();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save device');
    }
  };

  // Delete device
  const handleDelete = async (id: string) => {
    try {
      await zkDeviceApi.delete(id);
      message.success('Device deleted successfully');
      fetchDevices();
    } catch (error) {
      message.error('Failed to delete device');
    }
  };

  // Toggle enabled
  const handleToggleEnabled = async (device: ZkDevice) => {
    try {
      await zkDeviceApi.setEnabled(device.id, !device.isEnabled);
      message.success(`Device ${device.isEnabled ? 'disabled' : 'enabled'}`);
      fetchDevices();
    } catch (error) {
      message.error('Failed to update device');
    }
  };

  // Status badge
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'connected':
        return <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>;
      case 'disconnected':
        return <Tag icon={<CloseCircleOutlined />} color="default">Disconnected</Tag>;
      case 'error':
        return <Tag icon={<ExclamationCircleOutlined />} color="error">Error</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Table columns
  const columns: ColumnsType<ZkDevice> = [
    {
      title: 'Device Name',
      dataIndex: 'deviceName',
      key: 'deviceName',
      render: (name, record) => (
        <Space>
          <DesktopOutlined />
          <Text strong={record.isEnabled} type={record.isEnabled ? undefined : 'secondary'}>
            {name}
          </Text>
          {!record.isEnabled && <Tag>Disabled</Tag>}
        </Space>
      )
    },
    {
      title: 'IP Address',
      key: 'address',
      render: (_, record) => (
        <Text code>{record.ipAddress}:{record.port}</Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      render: (date) => date ? (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(date).fromNow()}
        </Tooltip>
      ) : <Text type="secondary">Never</Text>
    },
    {
      title: 'Sync Interval',
      dataIndex: 'syncInterval',
      key: 'syncInterval',
      render: (minutes) => `${minutes} min`
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Test Connection">
            <Button
              type="text"
              icon={<ApiOutlined />}
              loading={testingConnection === record.id}
              onClick={() => handleTestConnection(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditDevice(record)}
            />
          </Tooltip>
          <Tooltip title={record.isEnabled ? 'Disable' : 'Enable'}>
            <Switch
              size="small"
              checked={record.isEnabled}
              onChange={() => handleToggleEnabled(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this device?"
            description="All attendance data from this device will also be deleted."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Statistics
  const enabledDevices = devices.filter(d => d.isEnabled).length;
  const connectedDevices = devices.filter(d => d.status === 'connected').length;

  // Show error state
  if (error && !loading) {
    return (
      <Result
        status="error"
        title="Failed to Load Devices"
        subTitle={error}
        extra={[
          <Button key="retry" type="primary" onClick={fetchDevices}>
            Retry
          </Button>,
          <Alert
            key="help"
            type="info"
            showIcon
            style={{ marginTop: 16, textAlign: 'left' }}
            message="Setup Required"
            description={
              <div>
                <p>Make sure you have run the following commands in the backend folder:</p>
                <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
{`cd backend
npm install zkteco-js
npx prisma generate
npx prisma db push`}
                </pre>
                <p>Then restart the backend server.</p>
              </div>
            }
          />
        ]}
      />
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Devices"
              value={devices.length}
              prefix={<DesktopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Enabled"
              value={enabledDevices}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Connected"
              value={connectedDevices}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Disconnected"
              value={enabledDevices - connectedDevices}
              valueStyle={{ color: connectedDevices < enabledDevices ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Toolbar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddDevice}
        >
          Add Device
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchDevices}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={devices}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingDevice ? 'Edit Device' : 'Add Device'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={500}
        okText="Save"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="deviceName"
            label="Device Name"
            rules={[{ required: true, message: 'Please enter device name' }]}
          >
            <Input placeholder="e.g., Main Office Entrance" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="ipAddress"
                label="IP Address"
                rules={[
                  { required: true, message: 'Please enter IP address' },
                  { pattern: /^(\d{1,3}\.){3}\d{1,3}$/, message: 'Invalid IP address format' }
                ]}
              >
                <Input placeholder="e.g., 192.168.1.100" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="port"
                label="Port"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={65535} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Button
              icon={<ApiOutlined />}
              onClick={() => handleTestConnection()}
              loading={testingConnection === 'new'}
            >
              Test Connection
            </Button>
          </div>

          <Form.Item
            name="deviceSerial"
            label="Device Serial"
            tooltip="Will be auto-filled after successful connection test"
          >
            <Input placeholder="Device serial number" disabled />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="syncInterval"
                label="Sync Interval (minutes)"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={1440} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="timezone"
                label="Timezone"
              >
                <Input placeholder="e.g., Asia/Hong_Kong" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isEnabled"
            label="Enabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ZKDeviceList;
